import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { getSearchFormStyles } from '../utils/styleUtils';
import styles from './GoogleCalendarSync.module.css';

const TEXT = {
  title: { ko: 'Google Calendar 연동', en: 'Google Calendar Sync', fr: 'Synchronisation Google Calendar' },
  status: { ko: '연동 상태', en: 'Connection Status', fr: 'Statut de connexion' },
  connected: { ko: '연동됨', en: 'Connected', fr: 'Connecté' },
  disconnected: { ko: '연동 안됨', en: 'Not Connected', fr: 'Non connecté' },
  lastSync: { ko: '마지막 동기화', en: 'Last Sync', fr: 'Dernière synchronisation' },
  connect: { ko: 'Google Calendar 연동', en: 'Connect Google Calendar', fr: 'Connecter Google Calendar' },
  disconnect: { ko: '연동 해제', en: 'Disconnect', fr: 'Déconnecter' },
  loading: { ko: '처리 중...', en: 'Processing...', fr: 'Traitement...' },
  confirmDisconnect: { ko: 'Google Calendar 연동을 해제하시겠습니까?', en: 'Are you sure you want to disconnect Google Calendar?', fr: 'Êtes-vous sûr de vouloir déconnecter Google Calendar?' },
  syncSuccess: { ko: '동기화가 완료되었습니다.', en: 'Synchronization completed.', fr: 'Synchronisation terminée.' },
  syncFailed: { ko: '동기화에 실패했습니다.', en: 'Synchronization failed.', fr: 'Échec de la synchronisation.' },
  manualSync: { ko: '수동 동기화', en: 'Manual Sync', fr: 'Synchronisation manuelle' },
  calendars: { ko: 'GNEGNEGNE.FR 캘린더', en: 'GNEGNEGNE.FR Calendar', fr: 'Calendrier GNEGNEGNE.FR' },
  noCalendars: { ko: 'GNEGNEGNE.FR 캘린더가 없습니다.', en: 'No GNEGNEGNE.FR calendar found.', fr: 'Aucun calendrier GNEGNEGNE.FR trouvé.' },
  refreshCalendars: { ko: '캘린더 목록 새로고침', en: 'Refresh Calendar List', fr: 'Actualiser la liste des calendriers' },
  createCalendar: { ko: 'GNEGNEGNE.FR 캘린더 생성', en: 'Create GNEGNEGNE.FR Calendar', fr: 'Créer le calendrier GNEGNEGNE.FR' },
  calendarCreated: { ko: 'GNEGNEGNE.FR 캘린더가 생성되었습니다!', en: 'GNEGNEGNE.FR calendar has been created!', fr: 'Le calendrier GNEGNEGNE.FR a été créé !' },
  calendarCreateFailed: { ko: '캘린더 생성에 실패했습니다.', en: 'Failed to create calendar.', fr: 'Échec de la création du calendrier.' },
  calendarDescription: {
    ko: 'GNEGNEGNE.FR 전용 캘린더에만 이벤트가 동기화됩니다.',
    en: 'Events are synchronized only to the GNEGNEGNE.FR dedicated calendar.',
    fr: 'Les événements sont synchronisés uniquement avec le calendrier dédié GNEGNEGNE.FR.'
  },
  // 추가 다국어 텍스트
  authFailed: { ko: 'Google Calendar 연동에 실패했습니다.', en: 'Failed to connect to Google Calendar.', fr: 'Échec de la connexion à Google Calendar.' },
  disconnectSuccess: { ko: 'Google Calendar 연동이 해제되었습니다.', en: 'Google Calendar has been disconnected.', fr: 'Google Calendar a été déconnecté.' },
  disconnectFailed: { ko: '연동 해제에 실패했습니다.', en: 'Failed to disconnect.', fr: 'Échec de la déconnexion.' },
  unknownError: { ko: '알 수 없는 오류', en: 'Unknown error', fr: 'Erreur inconnue' },
  refreshIcon: { ko: '🔄', en: '🔄', fr: '🔄' }
};

export default function GoogleCalendarSync() {
  const { userInfo } = useContext(UserContext);
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);

  const lang = userInfo?.preferred_language || 'ko';

  // 공통 스타일 가져오기
  const searchStyles = getSearchFormStyles();

  // 권한 확인
  const canUseGoogleSync = userInfo?.level === 'admin' ||
    userInfo?.level === 'staff_artist' ||
    userInfo?.level === 'guest_artist';

  // 연동 상태 조회
  const checkSyncStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/calendar/sync-status', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
        setIsConnected(data.isConnected || false);
        setLastSyncedAt(data.lastSyncedAt);
      }
    } catch (error) {
      console.error('연동 상태 조회 실패:', error);
    }
  }, []);

  // 캘린더 목록 조회 (GNEGNEGNE.FR만 필터링)
  const fetchCalendars = useCallback(async () => {
    setIsLoadingCalendars(true);
    try {
      const response = await fetch('/api/calendar/list', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // GNEGNEGNE.FR 캘린더만 필터링
        const gnegnegneCalendars = (data.calendars || []).filter(calendar =>
          calendar.summary && calendar.summary.includes('GNEGNEGNE.FR')
        );
        setCalendars(gnegnegneCalendars);
      }
    } catch (error) {
      console.error('캘린더 목록 조회 실패:', error);
    } finally {
      setIsLoadingCalendars(false);
    }
  }, []);

  // Google Calendar 연동 시작
  const startGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/calendar/google-auth-url', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.authUrl) {
          window.location.href = data.authUrl;
        }
      }
    } catch (error) {
      console.error('Google Calendar 연동 실패:', error);
      alert(TEXT.authFailed[lang]);
    } finally {
      setIsLoading(false);
    }
  };

  // 연동 해제
  const disconnectGoogle = async () => {
    if (!window.confirm(TEXT.confirmDisconnect[lang])) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/calendar/disconnect', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setIsConnected(false);
        setLastSyncedAt(null);
        setCalendars([]);
        alert(TEXT.disconnectSuccess[lang]);
      }
    } catch (error) {
      console.error('연동 해제 실패:', error);
      alert(TEXT.disconnectFailed[lang]);
    } finally {
      setIsLoading(false);
    }
  };

  // 수동 동기화
  const manualSync = async () => {
    setIsLoading(true);

    // 백엔드 API 호환성을 위한 요청 데이터
    const requestData = {
      action: 'sync_all',
      userId: userInfo?.user_id,
      forceSync: true,
      eventId: null, // 전체 동기화를 위해 null로 설정
      syncAllEvents: true // 전체 이벤트 동기화 플래그
    };

    console.log('수동 동기화 요청 데이터:', requestData);

    try {
      const response = await fetch('/api/calendar/sync-to-google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || TEXT.syncSuccess[lang]);
        checkSyncStatus();
        fetchCalendars();
      } else {
        const errorData = await response.json();
        console.error('수동 동기화 응답 에러:', errorData);
        alert(TEXT.syncFailed[lang] + ': ' + (errorData.message || TEXT.unknownError[lang]));
      }
    } catch (error) {
      console.error('수동 동기화 실패:', error);
      alert(TEXT.syncFailed[lang] + ': ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // GNEGNEGNE.FR 캘린더 생성
  const createGnegnegneCalendar = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/calendar/create-calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId: userInfo?.user_id,
          calendarName: 'GNEGNEGNE.FR',
          description: 'Gnegnegne 타투 스튜디오 예약 및 이벤트'
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || TEXT.calendarCreated[lang]);
        checkSyncStatus();
        fetchCalendars();
      } else {
        const errorData = await response.json();
        console.error('캘린더 생성 응답 에러:', errorData);
        alert(TEXT.calendarCreateFailed[lang] + ': ' + (errorData.message || TEXT.unknownError[lang]));
      }
    } catch (error) {
      console.error('캘린더 생성 실패:', error);
      alert(TEXT.calendarCreateFailed[lang] + ': ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canUseGoogleSync) {
      checkSyncStatus();
    }
  }, [canUseGoogleSync, checkSyncStatus]);

  // URL 파라미터 변경 감지하여 연동 상태 새로고침
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const googleAuth = urlParams.get('google_auth');

    if (googleAuth === 'success' && canUseGoogleSync) {
      // OAuth 성공 시 연동 상태 새로고침
      const timeoutId = setTimeout(() => {
        checkSyncStatus();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [canUseGoogleSync, checkSyncStatus, location.search]);

  // 연동 상태가 변경되면 캘린더 목록 조회
  useEffect(() => {
    if (isConnected) {
      fetchCalendars();
    }
  }, [fetchCalendars, isConnected]);

  // 권한이 없으면 렌더링하지 않음
  if (!canUseGoogleSync) {
    return null;
  }

  return (
    <div className={styles.googleCalendarSync} style={{
      border: '1px solid #000',
      background: 'transparent',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '20px',
      width: '80vw',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <h3>{TEXT.title[lang]}</h3>

      <div className={styles.syncStatus}>
        <div className={styles.statusItem}>
          <span className={styles.label}>{TEXT.status[lang]}:</span>
          <span className={`${styles.value} ${isConnected ? styles.connected : styles.disconnected}`}>
            {isConnected ? TEXT.connected[lang] : TEXT.disconnected[lang]}
          </span>
        </div>

        {lastSyncedAt && (
          <div className={styles.statusItem}>
            <span className={styles.label}>{TEXT.lastSync[lang]}:</span>
            <span className={styles.value}>
              {new Date(lastSyncedAt).toLocaleString(
                lang === 'ko' ? 'ko-KR' :
                  lang === 'fr' ? 'fr-FR' :
                    'en-GB',
                {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                }
              )}
            </span>
          </div>
        )}
      </div>

      {/* GNEGNEGNE.FR 캘린더 목록 */}
      {isConnected && (
        <div className={styles.calendarList}>
          <div className={styles.calendarHeader}>
            <h4>{TEXT.calendars[lang]}</h4>
            <div className={searchStyles.searchForm}>
              <button
                onClick={fetchCalendars}
                disabled={isLoadingCalendars}
              >
                {isLoadingCalendars ? '...' : TEXT.refreshIcon[lang]}
              </button>
            </div>
          </div>
          <div className={styles.calendarDescription}>
            <p>{TEXT.calendarDescription[lang]}</p>
          </div>

          {calendars.length > 0 ? (
            <div className={styles.calendarItems}>
              {calendars.map(calendar => (
                <div key={calendar.id} className={styles.calendarItem}>
                  <div
                    className={styles.calendarColor}
                    style={{ backgroundColor: calendar.backgroundColor || '#4285f4' }}
                  />
                  <span className={styles.calendarName}>{calendar.summary}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noCalendars}>
              {TEXT.noCalendars[lang]}
            </div>
          )}
        </div>
      )}

      <div style={{ textAlign: 'center' }}>
        {!isConnected ? (
          <div className={searchStyles.searchForm}>
            <button
              onClick={startGoogleAuth}
              disabled={isLoading}
            >
              {isLoading ? TEXT.loading[lang] : TEXT.connect[lang]}
            </button>
          </div>
        ) : (
          <>
            <div className={searchStyles.searchForm} style={{ marginBottom: '12px' }}>
              <button
                onClick={createGnegnegneCalendar}
                disabled={isLoading}
              >
                {isLoading ? TEXT.loading[lang] : TEXT.createCalendar[lang]}
              </button>
            </div>
            <div className={searchStyles.searchForm}>
              <button
                onClick={manualSync}
                disabled={isLoading}
              >
                {isLoading ? TEXT.loading[lang] : TEXT.manualSync[lang]}
              </button>
              <button
                onClick={disconnectGoogle}
                disabled={isLoading}
              >
                {TEXT.disconnect[lang]}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 
